import { Language } from '../types/Antiloop.js'
import { getCompactTextEntry, getFlatDesignSystemStyle, hasCompactText } from './CompactTextRepository.js'

/**
 * Get text from the new compact text system
 * Only works with string keys from compact-texts.json
 * Supports customer-specific translation overrides
 *
 * Fallback chain:
 * 1. {language}-{customerId} - Customer-specific translation for requested language
 * 2. {language} - Standard translation for requested language
 * 3. sv-{customerId} - Customer-specific Swedish translation
 * 4. sv - Standard Swedish translation (ultimate fallback)
 */
export const getGrooverText = (textKey: string | any, language: Language, customerId?: string): string => {
  // Handle legacy TextKeyWithOnlyText objects
  if (typeof textKey === 'object' && textKey !== null) {
    return textKey.texts?.[language] || textKey.characters || ''
  }

  // Handle string keys
  if (typeof textKey === 'string') {
    const compactEntry = getCompactTextEntry(textKey)
    if (compactEntry) {
      // 1. Try customer-specific translation for requested language
      if (customerId && compactEntry.c?.[`${language}-${customerId}`]) {
        return compactEntry.c[`${language}-${customerId}`]
      }

      // 2. Try standard language translation
      if (compactEntry.l?.[language]) {
        return compactEntry.l[language]
      }

      // 3. Try customer-specific Swedish translation
      if (customerId && compactEntry.c?.[`sv-${customerId}`]) {
        return compactEntry.c[`sv-${customerId}`]
      }

      // 4. Ultimate fallback to Swedish
      return compactEntry.l?.sv || ''
    }

    console.warn(`Text key "${textKey}" not found in compact-texts.json`)
    return textKey
  }

  return ''
}

/**
 * Get text style from the new compact text system
 * Only works with string keys from compact-texts.json
 */
export function getGrooverTextStyle(styleKey: string): Record<string, any> {
  const style = getFlatDesignSystemStyle(styleKey)
  if (style) {
    return style
  }

  if (process.env.NODE_ENV !== 'production') {
    console.warn(`Style not found: ${styleKey}`)
  }
  return {}
}

/**
 * Check if a text key exists in the new compact format
 */
export const hasGrooverText = (key: string): boolean => {
  return hasCompactText(key)
}

/**
 * Format text with parameters (same as the old formatText function)
 */
export type Printable = string | number | undefined

export type Options = {
  boldParams?: boolean
  boldParamsIndex?: number
  splitCharacter?: string
}

export const formatGrooverText = (
  text: string,
  params: Printable[],
  { boldParams, boldParamsIndex }: Options = {}
): string => {
  if (!params || params.length === 0) return text

  // Prepare params with bolding if needed
  const _params = params.map((p, i) => {
    const str = p?.toString() || ''
    if (boldParams) return makeBold(str)
    if (boldParamsIndex === i) return makeBold(str)
    return str
  })

  // Support {0}, {1}, {2} style placeholders in addition to the legacy
  // "xxx", "xxx2", ... format.
  if (/\{\d+}/.test(text)) {
    let formattedWithBraces = text

    _params.forEach((param, index) => {
      const placeholderRegex = new RegExp(`\\{${index}\\}`, 'g')
      formattedWithBraces = formattedWithBraces.replace(placeholderRegex, param)
    })

    return formattedWithBraces
  }

  // Replace all occurrences of xxx, xxx2, xxx3, ... with corresponding params.
  // IMPORTANT: Replace xxx2/xxx3/... first so "xxx" doesn't partially match them.
  let formatted = text

  // Replace xxx2, xxx3, ... (1-indexed, with xxx = first param)
  for (let i = _params.length - 1; i >= 1; i -= 1) {
    const param = _params[i]
    formatted = formatted.replace(new RegExp(`xxx${i + 1}`, 'g'), param)
  }

  // Replace xxx (but not xxx2/xxx3/...)
  formatted = formatted.replace(/xxx(?!\d)/g, _params[0])

  return formatted
}

export function makeBold(p: string): string {
  return `<b>${p}</b>`
}
